import { useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { useTranslation } from 'react-i18next';
import useGooglePlaces from 'GooglePlaces/useGooglePlaces';
import { type PlaceDetails } from 'GooglePlaces';
import { locationString } from 'Location';
import { ProgramTypeConfigs } from 'Program/ProgramTypeConfigs';
import { type ProgramType } from 'Program';
import { InstitutionId } from 'Institutions';
import { AdvancedFilters } from './AdvancedFilters';
import { type FilterControlsProps } from './FilterControls.types';
import { type FilterTab } from '../../hooks/usePeopleFilters.types';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type DropdownOption = { key: string; label: string };
type DropdownGroup = { label: string; options: DropdownOption[] };

const TABS: { key: FilterTab; labelKey: string }[] = [
    { key: 'all', labelKey: 'all' },
    { key: 'students', labelKey: 'students' },
    { key: 'alumni', labelKey: 'alumni' },
    { key: 'myClass', labelKey: 'myClass' },
];

// Build network-enabled program type options grouped by institution/type
function buildProgramOptionGroups(): DropdownGroup[] {
    const allProgramTypes = (Object.keys(ProgramTypeConfigs) as ProgramType[]).filter(
        pt => ProgramTypeConfigs[pt].supportsNetworkAccess,
    );

    const toOption = (pt: ProgramType): DropdownOption => ({
        key: pt,
        label: (ProgramTypeConfigs[pt].shortProgramTitle as string) ?? pt,
    });

    const sortByLabel = (a: DropdownOption, b: DropdownOption) => a.label.localeCompare(b.label);

    const quanticDegree = allProgramTypes
        .filter(
            pt =>
                ProgramTypeConfigs[pt].isDegreeProgram &&
                ProgramTypeConfigs[pt].institutionID === InstitutionId.quantic,
        )
        .map(toOption)
        .sort(sortByLabel);

    const valarDegree = allProgramTypes
        .filter(
            pt =>
                ProgramTypeConfigs[pt].isDegreeProgram && ProgramTypeConfigs[pt].institutionID === InstitutionId.valar,
        )
        .map(toOption)
        .sort(sortByLabel);

    const execEd = allProgramTypes
        .filter(pt => ProgramTypeConfigs[pt].isExecEd)
        .map(toOption)
        .sort(sortByLabel);

    return [
        { label: 'quanticDegreePrograms', options: quanticDegree },
        { label: 'valarDegreePrograms', options: valarDegree },
        { label: 'execEdPrograms', options: execEd },
    ].filter(g => g.options.length > 0);
}

const PROGRAM_OPTION_GROUPS = buildProgramOptionGroups();

function FilterSearchInput({
    icon,
    placeholder,
    value,
    onChange,
    inputRef,
}: {
    icon: typeof faMagnifyingGlass;
    placeholder: string;
    value?: string;
    onChange?: (value: string) => void;
    inputRef?: React.Ref<HTMLDivElement>;
}) {
    return (
        <div
            ref={inputRef}
            className="flex h-12 items-center gap-3 rounded-[8px] border border-transparent bg-slate-grey-50 px-4 transition-colors hover:border-slate-grey-300"
        >
            <FontAwesomeIcon icon={icon} className="w-4 text-base text-slate-grey" />
            <input
                type="text"
                {...(value !== undefined ? { value, onChange: e => onChange?.(e.target.value) } : {})}
                placeholder={placeholder}
                className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange?.('')}
                    className="shrink-0 text-slate-grey-300 hover:text-slate-grey"
                >
                    <FontAwesomeIcon icon={faCircleXmark} className="text-base" />
                </button>
            )}
        </div>
    );
}

function SelectedChips({ items, onRemove }: { items: string[]; onRemove: (index: number) => void }) {
    if (items.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
                <span
                    key={item}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-grey-100 px-2.5 py-1 text-xs text-black"
                >
                    {item}
                    <button type="button" onClick={() => onRemove(i)} className="text-slate-grey hover:text-black">
                        <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                    </button>
                </span>
            ))}
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- dropdown with inline chips
function MultiSelectDropdown({
    label,
    placeholder,
    options,
    groups,
    selected,
    onChange,
    searchable,
}: {
    label: string;
    placeholder: string;
    options?: DropdownOption[];
    groups?: { label: string; options: DropdownOption[] }[];
    selected: string[];
    onChange: (selected: string[]) => void;
    searchable?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const allOptions = groups ? groups.flatMap(g => g.options) : (options ?? []);

    const filteredOptions =
        searchable && search
            ? (options ?? []).filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
            : options;

    const toggle = (key: string) => {
        if (selected.includes(key)) {
            onChange(selected.filter(k => k !== key));
        } else {
            onChange([...selected, key]);
        }
    };

    const selectedItems = selected.map(key => ({
        key,
        label: allOptions.find(o => o.key === key)?.label ?? key,
    }));

    const renderOption = (option: DropdownOption) => (
        <button
            key={option.key}
            type="button"
            onClick={() => toggle(option.key)}
            className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-slate-grey-100"
        >
            <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                    selected.includes(option.key)
                        ? 'border-black bg-black text-white'
                        : 'border-slate-grey-200 bg-white'
                }`}
            >
                {selected.includes(option.key) && <FontAwesomeIcon icon={faCheck} className="text-[10px]" />}
            </span>
            <span className="text-base text-black">{option.label}</span>
        </button>
    );

    return (
        <div>
            <button
                type="button"
                onClick={() => {
                    if (isOpen) setSearch('');
                    setIsOpen(!isOpen);
                }}
                className="flex min-h-14 w-full items-center justify-between rounded-[12px] border border-transparent bg-slate-grey-50 px-3 py-2 transition-colors hover:border-slate-grey-300"
            >
                <div className="flex min-w-0 flex-col items-start">
                    <span className="text-sm text-black">{label}</span>
                    {selectedItems.length > 0 ? (
                        <span className="flex flex-wrap gap-1 pt-0.5">
                            {selectedItems.map(item => (
                                <span
                                    key={item.key}
                                    className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs text-black"
                                >
                                    {item.label}
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={e => {
                                            e.stopPropagation();
                                            toggle(item.key);
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.stopPropagation();
                                                toggle(item.key);
                                            }
                                        }}
                                        className="text-slate-grey hover:text-black"
                                    >
                                        <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                                    </span>
                                </span>
                            ))}
                        </span>
                    ) : (
                        <span className="text-base text-slate-grey">{placeholder}</span>
                    )}
                </div>
                <FontAwesomeIcon
                    icon={isOpen ? faChevronUp : faChevronDown}
                    className="ms-2 shrink-0 text-sm text-black"
                />
            </button>
            {isOpen && (
                <div className="mt-1 max-h-60 overflow-y-auto rounded-[8px] bg-slate-grey-50 py-1">
                    {searchable && (
                        <div className="sticky top-0 bg-slate-grey-50 px-3 pb-1 pt-1">
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={placeholder}
                                className="w-full rounded-[6px] border border-slate-grey-200 bg-white px-2.5 py-1.5 text-sm text-black outline-none placeholder:text-slate-grey"
                                // oxlint-disable-next-line jsx-a11y/no-autofocus -- focus search on open
                                autoFocus
                            />
                        </div>
                    )}
                    {groups
                        ? groups.map((group, i) => (
                              <div key={group.label}>
                                  {i > 0 && <div className="mx-3 my-1 h-px bg-slate-grey-200" />}
                                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-grey">
                                      {group.label}
                                  </div>
                                  {group.options.map(renderOption)}
                              </div>
                          ))
                        : (filteredOptions ?? []).map(renderOption)}
                </div>
            )}
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- filter controls with many fields
export function FilterControls({
    keywordSearch,
    onKeywordSearchChange,
    activeTab,
    onTabChange,
    showAdvanced,
    onShowAdvancedChange,
    places,
    onPlacesChange,
    programTypes,
    onProgramTypesChange,
    companies,
    onCompaniesChange,
    industries,
    onIndustriesChange,
    interests,
    onInterestsChange,
    lookingFor,
    onLookingForChange,
    timezones,
    onTimezonesChange,
    hideSearch,
    hasActiveFilters,
    onReset,
}: FilterControlsProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.filters' });

    const cityInputElRef = useRef<HTMLInputElement | null>(null);

    const handlePlaceChanged = useCallback(
        (_place: unknown, placeDetails: PlaceDetails) => {
            if (placeDetails && (placeDetails.lat || placeDetails.formatted_address)) {
                onPlacesChange([...places, placeDetails]);
            }
            // Clear the input after a place is selected so the chip is the only indicator
            if (cityInputElRef.current) cityInputElRef.current.value = '';
        },
        [places, onPlacesChange],
    );

    const googlePlacesRef = useGooglePlaces({
        autoCompleteOptions: { types: ['(cities)'] },
        onPlaceChanged: handlePlaceChanged,
    });

    // Compose the Google Places callback ref with our own ref to the input element
    const cityInputRef = useCallback(
        (node: HTMLDivElement | null) => {
            googlePlacesRef(node);
            cityInputElRef.current = node?.querySelector('input') ?? null;
        },
        [googlePlacesRef],
    );

    const removePlace = useCallback(
        (index: number) => {
            onPlacesChange(places.filter((_, i) => i !== index));
        },
        [places, onPlacesChange],
    );

    const placeLabels = places.map(p => locationString(p) || p.formatted_address || 'Unknown');

    return (
        <div className="flex flex-col gap-5">
            {/* Search input */}
            {!hideSearch && (
                <FilterSearchInput
                    icon={faMagnifyingGlass}
                    placeholder={t('search')}
                    value={keywordSearch}
                    onChange={onKeywordSearchChange}
                />
            )}

            {/* City search with Google Places autocomplete */}
            <FilterSearchInput icon={faLocationDot} placeholder={t('citySearch')} inputRef={cityInputRef} />
            <div className="-mt-3">
                <SelectedChips items={placeLabels} onRemove={removePlace} />
            </div>

            {/* Filter tabs */}
            <div className="flex min-h-[42px] items-center justify-between rounded-[12px] bg-slate-grey-50 px-1.5 py-1">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => onTabChange(tab.key)}
                        className={`rounded-[8px] px-3 py-1 text-base transition-colors ${
                            activeTab === tab.key
                                ? 'border border-slate-grey-200 bg-white text-black shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]'
                                : 'text-slate-grey hover:text-black'
                        }`}
                    >
                        {t(tab.labelKey)}
                    </button>
                ))}
            </div>

            {/* Programs dropdown */}
            <MultiSelectDropdown
                label={t('programs')}
                placeholder={t('selectPrograms')}
                groups={PROGRAM_OPTION_GROUPS.map(g => ({ label: t(g.label), options: g.options }))}
                selected={programTypes}
                onChange={onProgramTypesChange}
            />

            {/* Advanced filters */}
            {showAdvanced && (
                <AdvancedFilters
                    companies={companies}
                    onCompaniesChange={onCompaniesChange}
                    industries={industries}
                    onIndustriesChange={onIndustriesChange}
                    interests={interests}
                    onInterestsChange={onInterestsChange}
                    lookingFor={lookingFor}
                    onLookingForChange={onLookingForChange}
                    timezones={timezones}
                    onTimezonesChange={onTimezonesChange}
                />
            )}

            {/* Show Advanced Filters toggle */}
            <button
                type="button"
                onClick={() => onShowAdvancedChange(!showAdvanced)}
                className="flex items-center gap-3"
            >
                <div
                    className={`flex h-7 w-12 items-center rounded-full p-0.5 transition-colors ${
                        showAdvanced ? 'bg-black' : 'bg-slate-grey-200'
                    }`}
                >
                    <div
                        className={`shadow-sm h-6 w-6 rounded-full bg-white transition-transform ${
                            showAdvanced ? 'ltr:translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
                        }`}
                    />
                </div>
                <span className="text-base text-slate-grey">{t('showAdvancedFilters')}</span>
            </button>

            {/* Reset link — visible only when filters are active */}
            {hasActiveFilters && onReset && (
                <button
                    type="button"
                    onClick={onReset}
                    className="self-start text-base text-black underline hover:text-primary"
                >
                    {t('reset')}
                </button>
            )}
        </div>
    );
}

export { MultiSelectDropdown, SelectedChips };
